import Page from '../../Page';
import React, { useEffect, useState } from "react";
import { Box, Container, IconButton, Skeleton, Tooltip, Stack, Button, Dialog, DialogContent, LinearProgress, Typography, Grid, Chip } from '@mui/material';
import MUIDataTable, { ExpandButton } from "mui-datatables";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CheckCircle, Close, Download, Error, ErrorOutline, Search, Update, Upload, Warning } from '@mui/icons-material';
import * as constants from 'src/utils/constants';
import { showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import { getDDMMYYYYFormat, getHHMMFormat } from 'src/utils/commonUtils';
import apiInstance from 'src/utils/apiAuth';
import CalendarRangePicker from 'src/components/CalendarRangePicker';
import sampleExcelFile from '../../../assets/sample_excel_file.xlsx';

const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                root: {
                    backgroundColor: '#red',
                },
                paper: {
                    boxShadow: 'none',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    // backgroundColor: 'yellow',
                },

            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: 'purple',
                    '& .MuiTableCell-content': {
                        textAlign: 'center',
                    },
                },
                // Center align the header cell content
                root: {
                    textAlign: 'center', // Center align all cells
                    '&.MuiTableCell-head': {
                        textAlign: 'center',
                    }
                }
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    fontWeight: '700',
                },
                data: {
                    fontWeight: '700',
                },
                contentWrapper: {
                    justifyContent: 'center', // Center the entire content wrapper
                },
            }
        },
        MUIDataTableSelectCell: {
            styleOverrides: {
                headerCell: {
                    // backgroundColor: 'blue',
                    fontWeight: '500px'
                },
            },
        },
        MuiTableFooter: {
            styleOverrides: {
                root: {
                    '& .MuiToolbar-root': {
                        // backgroundColor: 'purple',
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: "Lexend"
    }
});

const StyledButton = {
    backgroundColor: "#00AB55",
    fontFamily: "Lexend,Public Sans,sans-serif",
    fontWeight: "700",
    borderRadius: "8px",
    boxShadow: "0 8px 16px 0 rgb(0 171 85 / 10%)"
}

const components = {
    ExpandButton: function (props) {
        return <ExpandButton {...props} />;
    }
};

export default function ImportHistory() {
    const [isLoading, setIsLoading] = useState(true)
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [count, setCount] = useState(0);
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [filterParams, setFilterParams] = useState(null);
    const [sortOrder, setSortOrder] = useState();

    // Function to append filters to the URL
    const applyFiltersToUrl = (filterParams) => {
        let url = "";

        // use filterParams to filter the data using the API
        for (const key in filterParams) {
            if (filterParams[key] && Array.isArray(filterParams[key]) && filterParams[key].length > 0) {
                url += `${key}=${filterParams[key].join(",")}&`;
            }
        }
        return url;
    };

    const handleDownloadFile = (fileUrl) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        const date = new Date().toLocaleDateString().split('/').join('_');
        link.download = `Company_Data_${date}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileUrl);
    };

    const [userData, setUserData] = useState([]);
    const getUserData = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/users-viewset?short_details=${true}`;
        apiInstance.get(apiUrl, { headers: { Authorization: `Token ${localStorage.getItem("authToken")}` } })
            .then((response) => {
                let userData = response?.data?.data?.map((item) => ({
                    id: item.pk,
                    user: item.first_name + " " + item.last_name,
                }));
                setUserData(userData || []);
            })
            .catch((e) => console.log("something went wrong :(", e));
    };

    const options = {
        selectableRows: "none",
        download: false,
        filter: true,
        setFilterChipProps: (colIdx, colName, data) => {
            const column = columns.find(col => col.name === colName);
            const label = column ? column.label : colName; // Fallback to colName if no label is found

            return {
                label: `${label}: ${data}`,
            };
        },
        filterType: "multiselect",
        responsive: "standard",
        draggableColumns: { enabled: true },
        print: false,
        page: currentPage,
        serverSide: true,
        count: count,
        search: false,
        rowsPerPage: rowsPerPage,
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setCurrentPage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    break;
                case 'sort':
                    setSortOrder(tableState.sortOrder);
                    break;
                default:
                    break;
            }
        },
        onFilterChange: (column, filterList, type) => {
            const index = columns.findIndex((item) => item.name === column);
            const filterValue = filterList[index];

            setCurrentPage(0);

            const mapFilterValueToIds = (data, property = "name") => {
                return filterValue.length > 0
                    ? filterValue.map((item) => data.find((entry) => entry[property] === item).id)
                    : [];
            };

            let newFilterParams = { ...filterParams };

            switch (column) {
                case 'status':
                    newFilterParams.status = filterValue;
                    break;
                case 'created_by':
                    newFilterParams.created_by = mapFilterValueToIds(userData, "user")
                    break;
                case null:
                    newFilterParams = {};
                    break;
                default:
                    break;
            }

            // Remove the property if it has no value
            if (newFilterParams[column] && newFilterParams[column].length === 0) {
                delete newFilterParams[column];
            }

            setFilterParams(newFilterParams);
        },
    };
    
    const columns = [
        {
            name: "id",
            label: "ID",
            options: {
                filter: false,
                display: false,
            },
        },
        {
            name: "total_companies",
            label: "Companies",
            options: {
                filter: false,
            },
        },
        {
            name: 'company_statistics',
            label: "Result",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    return (
                        <Box display="flex" justifyContent="center" alignItems='center'>
                            <Tooltip title="Company Created" arrow>
                                <Box
                                    sx={{
                                        width: '30px',
                                        height: '30px',
                                        border: '2px solid #7ABD7E',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        marginRight: '8px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {value.created}
                                </Box>
                            </Tooltip>
                            <Tooltip title="Company Updated" arrow>
                                <Box
                                    sx={{
                                        width: '30px',
                                        height: '30px',
                                        border: '2px solid #F8D66D',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        marginRight: '8px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {value.updated}
                                </Box>
                            </Tooltip>
                            <Tooltip title="Company Failed" arrow>
                                <Box
                                    sx={{
                                        width: '30px',
                                        height: '30px',
                                        border: '2px solid #FF6961',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                    }}
                                >
                                    {value.failed}
                                </Box>
                            </Tooltip>
                        </Box>
                    )
                }
            },
        },
        {
            name: 'member_statistics',
            label: "Member",
            options: {
                filter: false,
                display: false,
                customBodyRender: (value) => {
                    return (
                        <Box display="flex" justifyContent="center" alignItems='center'>
                            <Tooltip title="Members Created" arrow>
                                <Box
                                    sx={{
                                        width: '30px',
                                        height: '30px',
                                        border: '2px solid #7ABD7E',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        marginRight: '8px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {value.created}
                                </Box>
                            </Tooltip>
                            <Tooltip title="Members Updated" arrow>
                                <Box
                                    sx={{
                                        width: '30px',
                                        height: '30px',
                                        border: '2px solid #F8D66D',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        marginRight: '8px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {value.updated}
                                </Box>
                            </Tooltip>
                            <Tooltip title="Members Failed" arrow>
                                <Box
                                    sx={{
                                        width: '30px',
                                        height: '30px',
                                        border: '2px solid #FF6961',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                    }}
                                >
                                    {value.failed}
                                </Box>
                            </Tooltip>
                        </Box>
                    )
                }
            },
        },
        {
            name: "created",
            label: "Created On",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {getDDMMYYYYFormat(value)} {getHHMMFormat(value, ":", false)} IST
                        </div>
                    )
                }
            }
        },
        {
            name: "status",
            label: "Status",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                    names: ["Pending", "Processing", "Completed With Errors", "Completed", "Failed"],
                },
                customBodyRender: (value) => {
                    const getStatusStyle = (status) => {
                        switch (status) {
                            case "Pending":
                                return { color: 'olive' };
                            case "Processing":
                                return { color: '#FF980E' };
                            case "Completed With Errors":
                                return { color: '#FF681E' };
                            case "Completed":
                                return { color: 'green' };
                            case "Failed":
                                return { color: '#D3212C' };
                            default:
                                return { color: 'black' };
                        }
                    };
        
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                ...getStatusStyle(value),
                            }}
                        >
                            {value}
                        </div>
                    );
                },
            },
        },
        {
            name: "error_log_file",
            label: "Error Log",
            options: {
                filter: false,
                customBodyRender: (value) => (
                    value ? 
                        <Box display='flex' justifyContent='center'>
                            <IconButton onClick={() => handleDownloadFile(value)}>
                                <Download color='error'/>
                            </IconButton>
                        </Box>
                        : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>-</div>
                ),
            },
        },
        {
            name: "created_by",
            label: "Created By",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                    names: userData.map((item) => item.user),
                },
                customBodyRender: (value) => {
                    return (
                        value ? value?.first_name + " " + value?.last_name : "PORTAL"
                    )
                }
            }
        },
    ];

    const [importData, setImportData] = useState([]);
    const getImportData = (page, pageSize = 10, selectedDateRange = [], filterParams = {}, sortOrder = null) => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/excel-import?`;
        if (page + 1 && pageSize) apiUrl += `page=${page + 1}&page_size=${pageSize}&`;
        if (sortOrder && sortOrder?.name && sortOrder?.direction) apiUrl += `sortfield=${sortOrder.name}&sortdirection=${sortOrder.direction}&`;
        if (selectedDateRange.length > 0) apiUrl += `${selectedDateRange[0] ? `start_date=${selectedDateRange[0]}` : ''}${selectedDateRange[1] ? `&end_date=${selectedDateRange[1]}` : ''}${selectedDateRange[2] ? `&date_filter_type=${selectedDateRange[2]}&` : ''}`;

        // Append filters to the URL
        apiUrl += applyFiltersToUrl(filterParams);

        if (apiUrl.endsWith("&")) apiUrl = apiUrl.slice(0, -1);

        apiInstance.get(apiUrl, { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } })
            .then((response) => {
                setImportData(response?.data?.results?.data);
                setCount(response?.data?.count || 0);
            })
            .catch((e) => showErrorToast(e))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        getImportData(currentPage, rowsPerPage, selectedDateRange, filterParams, sortOrder);
    }, [currentPage, rowsPerPage, selectedDateRange, filterParams, sortOrder]);

    useEffect(() => {
        getUserData();
    }, []);

    const handleDownloadSampleExcel = () => {
        const link = document.createElement('a');
        link.href = sampleExcelFile;
        link.download = 'Sample_Excel_File.xlsx';
        link.click();
    };

    const [isImportExcelDialogOpen, setIsImportExcelDialogOpen] = useState(false);
    const handleImportExcelClick = () => {
        setIsImportExcelDialogOpen(true);
    }

    const [excelImportSummary, setExcelImportSummary] = useState(null);
    const [errorExcelFileLink, setErrorExcelFileLink] = useState(null);
    const handleExcelFileUpload = () => {
        setExcelImportSummary(null);
        setErrorExcelFileLink(null);
        document.getElementById('excelFileInput').value = '';
        document.getElementById('excelFileInput').click();
    }

    const [importStatus, setImportStatus] = useState('Pending');
    const handleUploadExcel = (event) => {
        const selectedFile = event.target.files[0];
        const MAX_SIZE_MB = 50;
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
        if (!selectedFile) return; // No file selected

        setImportStatus('Uploading');

        if (selectedFile.size > MAX_SIZE_BYTES) {
            showErrorToast(null, "Please select a file smaller than 500 MB.");
            return;
        }

        const acceptedTypes = ['.xls', '.xlsx'];
        const isValidType = acceptedTypes.some(type => selectedFile.name.endsWith(type));

        if (!isValidType) {
            showErrorToast(null, "Only Excel file are allowed.");
            return;
        }

        callImportCompanyDataFromExcel(selectedFile);
    };

    const [isExcelImportProcessing, setIsExcelImportProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isExcelImportCompleted, setIsExcelImportCompleted] = useState(false);

    const callImportCompanyDataFromExcel = (selectedFile) => {
        setIsExcelImportProcessing(true);
        setImportStatus('Processing');

        const formData = new FormData();
        formData.append('file', selectedFile);

        apiInstance({
            method: "post",
            url: 'company/import_company_data_from_excel/',
            data: formData,
            headers: {
                Authorization: `Token ${localStorage.getItem('authToken')}`,
            },
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                const percent = Math.floor((loaded * 100) / total);
                setUploadProgress(percent);
            },
        })
            .then(function (response) {
                setImportStatus('Validating');
                if (response) {
                    if (response?.data?.errors?.length > 0) {
                        setImportStatus('Completed With Errors');
                    } else {
                        setImportStatus('Completed');
                    }
                    setExcelImportSummary(response?.data?.summary);
                    if (response?.data?.error_log_file) setErrorExcelFileLink(response?.data?.error_log_file);
                }
            })
            .catch(function (error) {
                showErrorToast(error);
                setImportStatus('Failed');
            })
            .finally(() => {
                setIsExcelImportProcessing(false);
                setIsExcelImportCompleted(true);
                setImportStatus(null);
            });
    };

    const handleDownloadErrorLog = () => {
        if (errorExcelFileLink) {
            apiInstance({
                url: `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${errorExcelFileLink}`,
                method: 'GET',
                responseType: 'blob',
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const date = new Date().toLocaleDateString().split('/').join('_');
                link.download = `Failed_Companies_Log_${date}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
        }
    };

    return (
        <Page title="Import History | CRM">
            <Container maxWidth="xl">
                <h2 align="center" style={{ fontSize: '25px', fontWeight: '800', marginBottom: '30px' }}>Import History</h2>
                {isLoading ? (
                    <div style={{ padding: '16px', marginTop: "20px" }}>
                        {/* Skeleton for the Table Header */}
                        <div style={{ display: 'flex', marginBottom: '20px' }}>
                            {columns.map((column, colIndex) => (
                                <Skeleton
                                    key={colIndex}
                                    variant="text"
                                    width={'30%'}
                                    style={{ marginRight: '16px' }}
                                    height={50}
                                />
                            ))}
                        </div>
                        {/* Skeletons for the Table Rows */}
                        {Array.from(new Array(rowsPerPage)).map((_, rowIndex) => (
                            <div key={rowIndex} style={{ display: 'flex', marginBottom: '10px' }}>
                                {columns.map((column, colIndex) => (
                                    <Skeleton
                                        key={colIndex}
                                        variant="text"
                                        width={'30%'}
                                        style={{ marginRight: '16px' }}
                                        height={50}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MUIDataTable
                            title={
                                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" width='100%' spacing={1}>
                                    <Stack direction={{ xs: "row", sm: "row" }} spacing={1}>
                                        <Tooltip title="Companies Created" arrow>
                                            <Box
                                                sx={{
                                                    paddingInline: '5px',
                                                    height: '30px',
                                                    border: '2px solid #7ABD7E',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'white',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                Created
                                            </Box>
                                        </Tooltip>
                                        <Tooltip title="Companies Updated" arrow>
                                            <Box
                                                sx={{
                                                    height: '30px',
                                                    paddingInline: '5px',
                                                    border: '2px solid #F8D66D',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'white',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                Updated
                                            </Box>
                                        </Tooltip>
                                        <Tooltip title="Companies Failed" arrow>
                                            <Box
                                                sx={{
                                                    height: '30px',
                                                    paddingInline: '5px',
                                                    border: '2px solid #FF6961',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '8px',
                                                    backgroundColor: 'white',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                Failed
                                            </Box>
                                        </Tooltip>
                                    </Stack>
                                    <Stack direction={{ xs: "row", sm: "row" }} spacing={1}>
                                        <Button
                                            size='small'
                                            variant="contained"
                                            startIcon={<Upload fontSize="small" />}
                                            style={StyledButton}
                                            onClick={() => handleImportExcelClick()}
                                        >
                                            Import Excel
                                        </Button>
                                        <CalendarRangePicker
                                            onChange={(value)=>{setSelectedDateRange(value)}}
                                            filterType="modified"
                                            labels={{
                                                start: {
                                                    label: "Created",
                                                    value: "modified"
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                            }
                            data={importData?.map(item => ({
                                id: item?.id,
                                status: item?.status,
                                total_companies: item?.total_companies,
                                company_statistics: {
                                    created: item?.companies_created || 0,
                                    updated: item?.companies_updated || 0,
                                    failed: item?.companies_failed || 0
                                },
                                member_statistics: {
                                    created: item?.members_created || 0,
                                    updated: item?.members_updated || 0,
                                    failed: item?.members_failed || 0
                                },
                                error_log_file: item?.error_log_file,
                                created_by: item?.created_by,
                                created: item?.modified,
                            }))}
                            columns={columns}
                            options={options}
                            components={components}
                        />
                    </ThemeProvider>
                )}
            {isImportExcelDialogOpen ?
                <Dialog maxWidth='sm' fullWidth open={isImportExcelDialogOpen} onClose={() => { setIsImportExcelDialogOpen(false); setExcelImportSummary(null); setErrorExcelFileLink(null); window.location.reload(); }}>
                    <DialogContent sx={{ px: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography align='center' variant="h5">Import Excel</Typography>
                            <IconButton onClick={() => { setIsImportExcelDialogOpen(false); setExcelImportSummary(null); setErrorExcelFileLink(null); window.location.reload(); }}>
                                <Close fontSize="medium" />
                            </IconButton>
                        </Stack>
                        <DialogContent>
                            <Stack direction='column' spacing={2}>
                                <Stack spacing={1} border='1px solid lightgrey' borderRadius='8px' padding={2}>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Typography><Error fontSize='medium' color='error' /></Typography>
                                        <Typography variant='subtitle1'>Important!</Typography>
                                    </Stack>
                                    <Stack ml='30px'>
                                        <Typography>Please ensure you upload the file in the exact format as the sample file. Otherwise, company data will not be added.</Typography>
                                    </Stack>
                                </Stack>
                                <Stack>
                                    {isExcelImportProcessing && (
                                        <Stack spacing={1} alignItems='center'>
                                            {importStatus}
                                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ width: '100%', mt: 2 }} />
                                        </Stack>
                                    )}
                                    {isExcelImportCompleted && excelImportSummary ? (
                                        <Stack spacing={2}>
                                            <Stack spacing={1} border='1px solid lightgrey' borderRadius='8px' padding={2}>
                                                <Typography variant="subtitle1">Icon Legend</Typography>
                                                <Stack direction="column" spacing={2}>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                        <Typography variant="caption">Created: Successfully added.</Typography>
                                                    </Stack>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <Update fontSize="medium" style={{ color: 'orange', marginLeft: -3 }} />
                                                        <Typography variant="caption">Updated: Information has been updated successfully.</Typography>
                                                    </Stack>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <ErrorOutline fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                        <Typography variant="caption">Failed: An error occurred during processing.</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            <Typography variant="h6">Import Summary</Typography>
                                            <Stack spacing={2} width='max-content'>
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent='space-between'>
                                                    <Typography>Company</Typography>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                        <Stack alignItems='center' direction='row' spacing={1}>
                                                            <Tooltip title="Created">
                                                                <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                            </Tooltip>
                                                            <Typography variant="h6">{excelImportSummary?.companies_created}</Typography>
                                                        </Stack>
                                                        <Stack alignItems='center' direction='row' spacing={1}>
                                                            <Tooltip title="Updated">
                                                                <Update fontSize="medium" style={{ color: 'orange', background: '' }} />
                                                            </Tooltip>
                                                            <Typography variant="h6">{excelImportSummary?.companies_updated}</Typography>
                                                        </Stack>
                                                        <Stack alignItems='center' direction='row' spacing={1}>
                                                            <Tooltip title="Failed">
                                                                <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                            </Tooltip>
                                                            <Typography variant="h6">{excelImportSummary?.companies_failed}</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent='space-between'>
                                                    <Typography>Members</Typography>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Created">
                                                                    <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.members_created}</Typography>
                                                            </Stack>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Updated">
                                                                    <Update fontSize="medium" style={{ color: 'orange', background: '' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.members_updated}</Typography>
                                                            </Stack>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Failed">
                                                                    <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.members_failed}</Typography>
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Typography variant="body1">Total Companies: <b>{excelImportSummary?.total_companies}</b></Typography>
                                            </Stack>
                                            {excelImportSummary?.failed_companies?.length > 0 && (
                                                <Stack spacing={1}>
                                                    <Stack direction='row' spacing={1} alignItems='center'>
                                                        <Typography><Warning fontSize='medium' color='warning' /></Typography>
                                                        <Typography variant='subtitle1'>Failed Companies</Typography>
                                                    </Stack>
                                                    <Grid item>
                                                        {excelImportSummary.failed_companies.map((company) => (
                                                            <Chip size='small' label={company} style={{ margin: 2 }} key={company} />
                                                        ))}
                                                    </Grid>
                                                </Stack>
                                            )}
                                        </Stack>
                                    ) : null}
                                </Stack>
                                {errorExcelFileLink && (
                                    excelImportSummary?.errors?.length > 0 && (
                                        <Stack spacing={1} width='max-content'>
                                            <Button 
                                                variant='text' 
                                                color='error'
                                                startIcon={<Download />}
                                                onClick={() => handleDownloadErrorLog()}
                                                style={{ background: 'rgba(255, 0, 0, 0.1)'}}
                                            >
                                                Download Error Log
                                            </Button>
                                        </Stack>
                                    )
                                )}
                                <Stack spacing={2} direction={{ xs: "column", sm: "row" }} justifyContent='center'>
                                    <Button
                                        variant="contained"
                                        startIcon={<Upload />}
                                        style={StyledButton}
                                        onClick={() => handleExcelFileUpload()}
                                    >Choose Excel File</Button>
                                    <input
                                        id="excelFileInput"
                                        type="file"
                                        accept=".xls, .xlsx"
                                        style={{ display: 'none' }}
                                        onChange={(event) => handleUploadExcel(event)}
                                    />
                                    <Button
                                        startIcon={<Download />}
                                        style={{ color: 'green', border: '1px solid green', borderRadius: '8px' }}
                                        onClick={() => handleDownloadSampleExcel()}
                                    >
                                        Download Sample Excel File
                                    </Button>
                                </Stack>
                            </Stack>
                        </DialogContent>
                    </DialogContent>
                </Dialog>
                : null}
            </Container>
        </Page>
    )
}